import React, { useState } from "react"
import { Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import PackCard from "@/features/billing/components/PackCard"
import { PAYMENT_OPTIONS } from "@/shared/constants/paymentCatalog"
import { startCheckout } from "@/features/billing/adapters/razorpayAdapter"
import { PurchasablePack } from "@/features/billing/adapters/types"

interface UpgradeDialogProps {
  open: boolean
  onClose: () => void
  reason?: string
}

const UpgradeDialog: React.FC<UpgradeDialogProps> = ({ open, onClose, reason }) => {
  const { showSnackbar } = useSnackbar()

  const [loading, setLoading] = useState(false)

  const packs = PAYMENT_OPTIONS.filter(option => ["pack_100", "pack_200"].includes(option.id)) as PurchasablePack[]
  const handlePurchase = (pack: PurchasablePack) => async () => {
    try {
      setLoading(true)
      await startCheckout(pack)
    } catch (error) {
      showSnackbar((error as Error)?.message ?? "Failed to start checkout. Please try again.", "error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      sx={{ "& .MuiDialog-paper": { margin: { xs: 2, sm: 4 } } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            Buy Image Credits
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack gap={{ xs: 1.5, sm: 2 }}>
          {reason && (
            <>
              <Typography variant="body2" color="text.secondary">
                {reason}
              </Typography>
              <Divider />
            </>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} gap={{ xs: 1.5, sm: 2 }}>
            {packs.map(pack => (
              <PackCard key={pack.id} pack={pack} onPurchase={handlePurchase(pack)} loading={loading} />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default UpgradeDialog
